import ErrorBoundary from "features/ErrorBoundary";
import { BaseTable, FilterSortPageType, FooterCalculatorEnum, GenericColumnModel, InteractionManager, FormatterCell, ValidatorFunc } from "../BaseTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "components/Button";
import { ColumnContainer, RowContainer } from "components/Layout";
import { calculateSum } from "../LocalTable/footerCalculationHelpers";
import { filterData, sortData } from "../LocalTable/filterSortPageHelpers";
import { SkeletonTable } from "../BaseTable/Skeleton/SkeletonTable";
import { Toolbar } from "../Toolbar/Toolbar";
import { ExportDataModel, FileResponse } from "services/tenantManagementService";
import { cloneColumnModel } from "features/TableColumns/usePrepareTableColumns";
import { ExportFormFlags } from "features/Export/Export";
import { ErrorType, FieldMessage, globalErrorKey } from "components/Form";
import { isRequiredValidator } from "./validators";
import { isEmptyObject } from "utils/objectUtil";
import { UnsavedChangesGuard } from "components/Form/UnsavedChangesGuard/UnsavedChangesGuard";

export type RowErrorsMap = {
	[columnId: string]: ErrorType
}
export type TableErrorsMap = {
	[rowDataId: number]: RowErrorsMap
} & {
	[globalErrorKey]?: ErrorType | undefined
}

type Props = {
	/** Row data model must contain field 'id' */
	rowsData: any[]
	initialRowsData: any[]
	footerRowsData?: any[]
	visibleColumns: GenericColumnModel[]
	hiddenColumns: GenericColumnModel[]
	filterSortPage: FilterSortPageType
	interactionManager: InteractionManager

	onChange(newRowsData: any[], oldRowData: any, columnId: string, newValue: any): any
	onSubmit?(): Promise<TableErrorsMap | void>
	onCancel?(): void
	onAdd?(): any
	onDelete?(id: number): void
	isDeleteable?: boolean
	isCreateable?: boolean
	isViewConfigurable?: boolean
	exportFunction?(model: ExportDataModel): Promise<FileResponse>
	exportTitle?: string
	exportFormFlags?: ExportFormFlags
	renderExportComponent?: () => React.ReactElement
	hideToolbar?: boolean
	renderAdditionalButtons?(disabled?: boolean, handleSubmitCallback?: () => void, isSubmitting?: boolean): any
}

const CrudTableWithoutSkeleton = ({
	rowsData,
	initialRowsData,
	footerRowsData,
	onChange,
	onSubmit,
	onCancel,
	onAdd,
	onDelete,
	isDeleteable,
	isCreateable,
	isViewConfigurable,
	exportFunction,
	exportTitle,
	renderAdditionalButtons,
	visibleColumns,
	hiddenColumns,
	filterSortPage,
	exportFormFlags,
	renderExportComponent,
	hideToolbar,
	interactionManager
}: Props) => {
	const [errorsMap, setErrorsMap] = useState<TableErrorsMap>({})
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [frozenRowsDataFilterSort, setFrozenRowsDataFilterSort] = useState<any[] | undefined>();

	useEffect(
		() => {
			setFrozenRowsDataFilterSort(undefined);
		},
		[filterSortPage]
	)

	const notFrozenRowsDataFilterSort = useMemo(
		() => {
			const filteredData = filterData(rowsData, filterSortPage.filters, visibleColumns)
			const filteredSortedData = sortData(filteredData, filterSortPage.sort, visibleColumns);
			return filteredSortedData;
		},
		[rowsData, filterSortPage.filters, filterSortPage.sort, visibleColumns]
	)

	const rowsDataFilterSort = frozenRowsDataFilterSort || notFrozenRowsDataFilterSort;

	const convertedColumns: GenericColumnModel[] = useMemo(
		() => visibleColumns.map((column) => {
			if (column.footerCalculator === FooterCalculatorEnum.SUM && column.footerValue === undefined) {
				// to perserve class type
				const clonedColumn = cloneColumnModel(column);
				clonedColumn.footerValue = calculateSum.bind(null, rowsDataFilterSort, column.id)
				return clonedColumn;
			}

			return column;
		}),
		[visibleColumns, rowsDataFilterSort]
	)

	const updateErrorsMapCallback = useCallback(
		(error: ErrorType | undefined, columnId: string, rowId: number) => {
			if (error) {
				// add error in errors map
				setErrorsMap((state) => {
					return {
						...state,
						[rowId]: {
							...state[rowId],
							[columnId]: error
						}
					}
				})
			} else {
				// delete error from errors map
				setErrorsMap((state) => {
					const rowErrorsMap = state[rowId];
					rowErrorsMap && delete rowErrorsMap[columnId];

					if (rowErrorsMap && isEmptyObject(rowErrorsMap)) {
						delete state[rowId];
						return state;
					}

					return {
						...state,
						[rowId]: rowErrorsMap
					}
				})
			}
		},
		[]
	)

	const validateCellCallback = useCallback(
		(validator: ValidatorFunc, rowData: any, columnId: string, value: any, isGlobalError?: boolean) => {
			const formatterCell: FormatterCell = {
				value,
				rowData: {
					...rowData,
					[columnId]: value
				},
				columnId
			}

			const error = validator(formatterCell);
			if (isGlobalError) {
				setErrorsMap((state) => {
					if (error) {
						return {
							...state,
							[globalErrorKey]: error
						}
					}

					return state;
				})
			} else {
				updateErrorsMapCallback(error, columnId, rowData.id);
			}

			return error === undefined;
		},
		[updateErrorsMapCallback]
	)

	const cellEditedCallback = useCallback(
		async (oldRowData: any, columnId: string, value: any) => {
			const column = visibleColumns.find((item) => item.id === columnId)!;

			const formatterCell: FormatterCell = {
				value,
				rowData: {
					...oldRowData,
					[columnId]: value
				},
				columnId
			}

			column.validator && validateCellCallback(column.validator, oldRowData, columnId, value);
			if (typeof column.isRequired === 'function' && column.isRequired(formatterCell)) {
				validateCellCallback(isRequiredValidator, oldRowData, columnId, value);
			}

			const newRowsData = rowsData.map((item) => {
				if (item.id === oldRowData.id) {
					return {
						...oldRowData,
						[columnId]: value
					}
				}

				return item;
			});

			const newRow = onChange(newRowsData, oldRowData, columnId, value);

			const newFilteredSortedData = rowsDataFilterSort.map((item) => {
				return newRow.id === item.id ? newRow : item;
			})

			setFrozenRowsDataFilterSort(newFilteredSortedData);
		},
		[visibleColumns, validateCellCallback, rowsData, onChange, rowsDataFilterSort]
	)

	const hasUnsavedChanges = useCallback(
		() => {
			if (isSubmitting || initialRowsData === rowsData) {
				return false;
			}

			if (initialRowsData.length !== rowsData.length) {
				return true;
			}

			for (let i = 0; i < rowsData.length; i++) {
				const rowData = rowsData[i];
				const initialRowData = initialRowsData[i];

				for (const key of Object.keys(rowData)) {
					const value = rowData[key] !== "" ? rowData[key] : undefined; // UserForm initials were "" and initialValue.initials is undefined
					const initialValue = initialRowData && (initialRowData[key] !== "" ? initialRowData[key] : undefined);

					// eslint-disable-next-line
					if (value != initialValue) {
						return true;
					}
				}
			}

			return false;
		},
		[initialRowsData, isSubmitting, rowsData]
	)

	const validateTableCallback = useCallback(
		() => {
			for (const column of visibleColumns) {
				if (typeof column.isRequired === 'function' && column.isRequired) {
					for (const rowData of rowsData) {
						const formatterCell: FormatterCell = {
							value: rowData[column.id],
							rowData,
							columnId: column.id
						}

						if (column.isRequired(formatterCell)) {
							const isValid = validateCellCallback(isRequiredValidator, rowData, column.id, rowData[column.id]);
							if (isValid === false) {
								return false;
							}
						}
					}
				}
				if (column.validator) {
					for (const rowData of rowsData) {
						const isValid = validateCellCallback(column.validator, rowData, column.id, rowData[column.id]);
						if (isValid === false) {
							return false;
						}
					}
				}
			}

			for (const column of hiddenColumns) {
				if (typeof column.isRequired === 'function' && column.isRequired) {
					for (const rowData of rowsData) {
						const formatterCell: FormatterCell = {
							value: rowData[column.id],
							rowData,
							columnId: column.id
						}

						if (column.isRequired(formatterCell)) {
							const isValid = validateCellCallback(isRequiredValidator, rowData, column.id, rowData[column.id], true);
							if (isValid === false) {
								return false;
							}
						}
					}
				}
			}

			return true;
		},
		[hiddenColumns, rowsData, validateCellCallback, visibleColumns]
	)

	const handleSubmitCallback = useCallback(
		async (): Promise<void> => {
			setIsSubmitting(true);

			if (validateTableCallback() && onSubmit) {
				let serverErrorsMap = await onSubmit();

				setErrorsMap(serverErrorsMap || {});

				if (serverErrorsMap === undefined) {
					setFrozenRowsDataFilterSort(undefined);
				}
			}

			setIsSubmitting(false);
		},
		[validateTableCallback, onSubmit]
	)

	const handleCancelCallback = useCallback(
		() => onCancel && onCancel(),
		[onCancel]
	)

	const addCallback = useCallback(
		() => {
			const newRowData = onAdd?.();
			setFrozenRowsDataFilterSort([
				...rowsDataFilterSort,
				newRowData
			]);
		},
		[rowsDataFilterSort, onAdd]
	)

	const deleteCallback = useCallback(
		(selectedRow: any | undefined) => {
			if (onDelete && selectedRow) {
				onDelete(selectedRow.id);

				if (errorsMap[selectedRow.id]) {
					delete errorsMap[selectedRow.id];
				}

				const indexOfDeleted = rowsDataFilterSort.findIndex((item) => item.id === selectedRow.id);
				setFrozenRowsDataFilterSort([
					...rowsDataFilterSort.slice(0, indexOfDeleted),
					...rowsDataFilterSort.slice(indexOfDeleted + 1, rowsDataFilterSort.length)
				]);
			}
		},
		[onDelete, errorsMap, rowsDataFilterSort]
	)

	const haveErrorsCallback = useCallback(
		() => {
			for (const rowId in errorsMap) {
				const rowErrorsMap = errorsMap[rowId];

				if (rowId === globalErrorKey || rowErrorsMap === undefined) {
					continue;
				}

				for (const columnId in rowErrorsMap) {
					const error = rowErrorsMap[columnId];

					if (error) {
						return true;
					}
				}
			}

			return false;
		},
		[errorsMap]
	)

	return (
		<ErrorBoundary location='CrudTable'>
			<ColumnContainer margin='medium'>
				{!hideToolbar &&
					<Toolbar
						visibleColumns={visibleColumns}
						hiddenColumns={hiddenColumns}
						filterSortPage={filterSortPage}
						interactionManager={interactionManager}
						addHandler={addCallback}
						deleteHandler={deleteCallback}
						isDeleteable={isDeleteable}
						isCreateable={isCreateable}
						isViewConfigurable={isViewConfigurable}
						exportFunction={exportFunction}
						exportTitle={exportTitle}
						exportFormFlags={exportFormFlags}
						renderExportComponent={renderExportComponent}
					/>
				}
				<ColumnContainer>
					<BaseTable
						columns={convertedColumns}
						rowsData={rowsDataFilterSort}
						footerRowsData={footerRowsData}
						filterSortPage={filterSortPage}
						// pagination={pagination}
						cellEdited={cellEditedCallback}
						disabledReorder={true}
						interactionManager={interactionManager}
						errorsMap={errorsMap}
					/>
					<RowContainer justifyContent="flex-end">
						<FieldMessage error={errorsMap[globalErrorKey]} />
					</RowContainer>
					{onSubmit &&
						<>
							<RowContainer justifyContent='flex-end'>
								<Button
									text='i18n.button.save'
									onClick={handleSubmitCallback}
									disabled={haveErrorsCallback()}
									isLoading={isSubmitting}
								/>
								{renderAdditionalButtons && renderAdditionalButtons(isSubmitting /*|| disabled*/, handleSubmitCallback, isSubmitting)}
								<Button
									text='i18n.button.reset'
									color='neutral'
									// disabled={disabled}
									onClick={handleCancelCallback}
								/>
							</RowContainer>

							{/* unsaved changes */}
							{/* {!disableUnsavedChangesGuard && */}
							<UnsavedChangesGuard shouldBlockNavigation={hasUnsavedChanges} />
						</>
					}
				</ColumnContainer>
			</ColumnContainer>
		</ErrorBoundary>
	)
}

export const CrudTable = ({ fetching, ...rest }: Props & { fetching?: boolean }) => {
	if (fetching) {
		return <SkeletonTable />
	}

	return <CrudTableWithoutSkeleton {...rest} />
}
